import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import ShareCourseButton from "@/components/ShareCourseButton";
import DemoLinkButton from "@/components/DemoLinkButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useConfirm } from "@/components/useConfirm";
import { CourseAnnounceDialog } from "@/components/CourseAnnounceDialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, FileCode, FileText, Sparkles, ArrowUpRight, Trash2, Eye, Settings2, Pencil, Check, X, GripVertical } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useLiveSync } from "@/lib/useLiveSync";

export default function CourseDetail() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [artifactType, setArtifactType] = useState("html");
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renamingBusy, setRenamingBusy] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  const load = async () => {
    const [c, m] = await Promise.all([
      api.get(`/courses/${courseId}`),
      api.get(`/modules?course_id=${courseId}`),
    ]);
    setCourse(c.data);
    setModules(m.data);
  };
  useEffect(() => { load(); }, [courseId]);

  // Live sync so a second admin tab / preview tab sees module add/delete/
  // reorder/content changes instantly. When the user themselves triggers an
  // update via this tab, the optimistic state stays and the SSE-driven reload
  // is just a redundant idempotent fetch.
  useLiveSync({ kind: "course", id: courseId, onUpdate: () => { if (!reordering) load(); } });

  const createModule = async () => {
    if (!title.trim()) return toast.error("Title required");
    setCreating(true);
    try {
      const { data } = await api.post("/modules", { course_id: courseId, title, artifact_type: artifactType });
      toast.success("Module created");
      setOpen(false); setTitle("");
      nav(`/admin/modules/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const togglePublish = async () => {
    const wasPublished = !!course.published;
    const { data } = await api.patch(`/courses/${courseId}`, { published: !course.published });
    setCourse(data);
    toast.success(data.published ? "Course published" : "Unpublished");
    // Auto-prompt: draft → live means learners can see it — offer to email them.
    if (data.published && !wasPublished) setAnnounceOpen(true);
  };

  const saveCourseDefault = async (next) => {
    try {
      const { data } = await api.patch(`/courses/${courseId}`, next);
      setCourse(data);
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const deleteModule = async (m) => {
    const ok = await confirm({
      title: `Delete module "${m.title}"?`,
      description: "This will permanently delete the module, its artifact, uploaded files, and chat history.",
      confirmLabel: "Delete module",
    });
    if (!ok) return;
    try {
      await api.delete(`/modules/${m.id}`);
      toast.success("Module deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const startRename = (m) => {
    setRenamingId(m.id);
    setRenameValue(m.title || "");
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };
  const commitRename = async () => {
    if (!renamingId) return;
    const trimmed = renameValue.trim();
    if (!trimmed) return toast.error("Title cannot be empty");
    const current = modules.find((m) => m.id === renamingId);
    if (current && current.title === trimmed) { cancelRename(); return; }
    setRenamingBusy(true);
    try {
      const { data } = await api.patch(`/modules/${renamingId}`, { title: trimmed });
      setModules((prev) => prev.map((m) => (m.id === renamingId ? { ...m, title: data.title } : m)));
      toast.success("Module renamed");
      cancelRename();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally { setRenamingBusy(false); }
  };

  // Drag-and-drop reorder: posts the full new ordering to the backend, which
  // also rewrites the "X.Y." title prefix to match the new index when the
  // title follows that convention.
  const persistOrder = async (next) => {
    setReordering(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/reorder-modules`, {
        module_ids: next.map((m) => m.id),
      });
      setModules(data.modules || next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      load(); // re-sync from server
    } finally { setReordering(false); }
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = modules.findIndex((m) => m.id === active.id);
    const newIndex = modules.findIndex((m) => m.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(modules, oldIndex, newIndex);
    setModules(next);   // optimistic
    persistOrder(next);
  };

  const sensors = useSensors(
    // Require a small drag distance so accidental row taps don't initiate DnD.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!course) {
    return <AdminLayout><div className="p-10 text-slate-500">Loading...</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="px-10 py-10 max-w-6xl">
        <Link to="/admin/courses" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft size={14} /> All courses
        </Link>

        <header className="flex items-end justify-between mb-8 gap-6">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Course</div>
            <h1 className="text-4xl font-display font-bold text-slate-900 tracking-tight">{course.title}</h1>
            <p className="text-slate-500 mt-2 max-w-2xl">{course.description || "No description"}</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <DemoLinkButton courseId={courseId} />
            <ShareCourseButton courseId={courseId} />
            <div className="flex items-center gap-3 px-4 py-2 bg-white border border-slate-200 rounded-md">
              <div>
                <div className="text-xs text-slate-500">Status</div>
                <div className={`text-sm font-medium ${course.published ? "text-emerald-700" : "text-slate-700"}`}>
                  {course.published ? "Published" : "Draft"}
                </div>
              </div>
              <Switch data-testid="course-publish-switch" checked={course.published} onCheckedChange={togglePublish} />
            </div>
          </div>
        </header>

        {/* Course-level defaults — inherited by NEW modules in this course */}
        <section className="mb-6">
          <div className="bg-slate-50/80 border border-slate-200 rounded-lg p-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="course-defaults-section">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <Settings2 size={13} className="text-[#E53935]"/> Course Defaults
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">Difficulty</span>
              <div className="flex items-center gap-1">
                {["easy", "medium", "hard"].map((d) => {
                  const active = (course.default_quiz_difficulty || "medium") === d;
                  return (
                    <button
                      key={d}
                      onClick={() => saveCourseDefault({ default_quiz_difficulty: d })}
                      data-testid={`course-default-difficulty-${d}`}
                      className={`text-[10px] uppercase tracking-wider font-semibold py-1 px-2.5 rounded border transition-colors ${
                        active
                          ? d === "easy" ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                            : d === "medium" ? "border-amber-300 bg-amber-50 text-amber-800"
                            : "border-red-300 bg-red-50 text-red-800"
                          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="course-default-auto-quiz-label">
              <input
                type="checkbox"
                checked={!!course.default_auto_quiz}
                onChange={(e) => saveCourseDefault({ default_auto_quiz: e.target.checked })}
                data-testid="course-default-auto-quiz"
                className="accent-[#E53935]"
              />
              <span className="text-xs text-slate-700">Auto-generate quiz on uploads</span>
            </label>
            <span className="text-[10px] text-slate-500 italic ml-auto">
              Applied to <strong>new</strong> modules created in this course
            </span>
          </div>
        </section>


        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-display font-semibold text-slate-900">Modules</h2>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-module-button" className="bg-blue-900 hover:bg-blue-800 text-white">
                  <Plus size={16} className="mr-1" /> New Module
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create new module</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <Label>Module Title</Label>
                    <Input data-testid="module-title-input" value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="e.g., Introduction to Acoustic Treatment" className="mt-1.5" />
                  </div>
                  <div>
                    <Label>Artifact Format</Label>
                    <Select value={artifactType} onValueChange={setArtifactType}>
                      <SelectTrigger data-testid="artifact-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="html">Rich HTML Lesson</SelectItem>
                        <SelectItem value="markdown">Markdown with Quiz</SelectItem>
                        <SelectItem value="embed">Claude HTML Artifact (upload)</SelectItem>
                        <SelectItem value="jsx">Claude JSX/React Artifact (upload)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1.5">You can change this anytime in the editor.</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button data-testid="create-module-submit" disabled={creating} onClick={createModule} className="bg-blue-900 hover:bg-blue-800 text-white">
                    {creating ? "Creating..." : "Create & Open"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {modules.length === 0 ? (
            <Card className="p-12 text-center border-dashed">
              <Sparkles size={36} className="mx-auto text-slate-300 mb-3" strokeWidth={1.3} />
              <div className="text-slate-700 font-medium">No modules yet</div>
              <div className="text-sm text-slate-500 mt-1">Add your first module and chat with AI to build it.</div>
            </Card>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={modules.map((m) => m.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2" data-testid="modules-sortable-list">
                  {modules.map((m, i) => (
                    <SortableModuleRow
                      key={m.id}
                      module={m}
                      index={i}
                      renamingId={renamingId}
                      renameValue={renameValue}
                      setRenameValue={setRenameValue}
                      renamingBusy={renamingBusy}
                      commitRename={commitRename}
                      cancelRename={cancelRename}
                      startRename={startRename}
                      deleteModule={deleteModule}
                      reordering={reordering}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>
      </div>
      <ConfirmDialog />
      <CourseAnnounceDialog open={announceOpen} onOpenChange={setAnnounceOpen} course={course} />
    </AdminLayout>
  );
}

// Single draggable module row. The drag handle (left grip) is the only DnD
// listener target — clicks on Preview / Delete / Open / Rename still work.
function SortableModuleRow({
  module: m, index: i, renamingId, renameValue, setRenameValue, renamingBusy,
  commitRename, cancelRename, startRename, deleteModule, reordering,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: m.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 30 : "auto",
  };
  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`p-4 border-slate-200 hover:border-blue-900/30 transition-colors group ${isDragging ? "shadow-lg ring-1 ring-blue-900/30" : ""}`}
      data-testid={`module-card-${m.id}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
          title="Drag to reorder"
          disabled={reordering}
          className="p-1.5 text-slate-300 hover:text-slate-700 cursor-grab active:cursor-grabbing touch-none disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`module-drag-handle-${m.id}`}
        >
          <GripVertical size={16} />
        </button>
        <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-900 font-semibold flex items-center justify-center text-sm shrink-0">
          {String(i + 1).padStart(2, "0")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {renamingId === m.id ? (
              <div className="flex items-center gap-1 flex-1 min-w-0" data-testid={`rename-module-row-${m.id}`}>
                <Input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                    else if (e.key === "Escape") { e.preventDefault(); cancelRename(); }
                  }}
                  disabled={renamingBusy}
                  className="h-7 text-sm font-medium flex-1 min-w-0"
                  data-testid={`rename-module-input-${m.id}`}
                />
                <button
                  onClick={commitRename}
                  disabled={renamingBusy}
                  className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                  title="Save (Enter)"
                  data-testid={`rename-module-save-${m.id}`}
                ><Check size={14}/></button>
                <button
                  onClick={cancelRename}
                  disabled={renamingBusy}
                  className="p-1 rounded text-slate-500 hover:bg-slate-100"
                  title="Cancel (Esc)"
                  data-testid={`rename-module-cancel-${m.id}`}
                ><X size={14}/></button>
              </div>
            ) : (
              <>
                <h3 className="font-medium text-slate-900 truncate" data-testid={`module-title-${m.id}`}>{m.title}</h3>
                <button
                  onClick={() => startRename(m)}
                  className="p-1 -ml-0.5 text-slate-300 hover:text-blue-900 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Rename module"
                  data-testid={`rename-module-${m.id}`}
                ><Pencil size={12}/></button>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${m.published ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                  {m.published ? "Live" : "Draft"}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              {m.artifact_type === "html" ? <FileCode size={12} /> : <FileText size={12} />}
              {m.artifact_type === "html" ? "HTML" : "Markdown"}
            </span>
            <span>{m.source_files?.length || 0} files</span>
            <span>{m.artifact_content ? `${m.artifact_content.length} chars` : "empty"}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/learn/modules/${m.id}?preview=1`} className="p-2 text-slate-400 hover:text-blue-900 transition-colors" title="Preview as learner" data-testid={`preview-module-${m.id}`}>
            <Eye size={15} />
          </Link>
          <button onClick={() => deleteModule(m)} className="p-2 text-slate-400 hover:text-red-600 transition-colors" title="Delete" data-testid={`delete-module-${m.id}`}>
            <Trash2 size={15} />
          </button>
          <Link to={`/admin/modules/${m.id}`} className="ml-1 inline-flex items-center gap-1 text-sm text-blue-900 font-medium hover:text-blue-700">
            Open <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </Card>
  );
}
